import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import supabase from '../supabase/supabase';
import { showRewardAd, showInterstitial, safeAlert } from '../utils/gameMonetize';

const GameContext = createContext();

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

const DEFAULT_GAME_STATS = {
  snake: { highScore: 0, gamesPlayed: 0 },
  flappy: { highScore: 0, gamesPlayed: 0 },
  memory: { highScore: 0, gamesPlayed: 0 },
  tetris: { highScore: 0, gamesPlayed: 0 },
  knife: { highScore: 0, gamesPlayed: 0 },
  solitaire: { highScore: 0, gamesPlayed: 0 },
  tentrix: { highScore: 0, gamesPlayed: 0 },
  brick: { highScore: 0, gamesPlayed: 0 },
  tower: { highScore: 0, gamesPlayed: 0 },
  tictactoe: { highScore: 0, gamesPlayed: 0 }
};

export const GameProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdModal, setShowAdModal] = useState(false);
  const [adReward, setAdReward] = useState(0);
  const [isGamePaused, setIsGamePaused] = useState(false);
  const [gameStats, setGameStats] = useState(() => {
    const savedStats = localStorage.getItem('prize_blaster_stats');
    return savedStats ? { ...DEFAULT_GAME_STATS, ...JSON.parse(savedStats) } : DEFAULT_GAME_STATS;
  });

  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // --- Auth & Data Sync ---
  useEffect(() => {
    let authSubscription = null;
    let realtimeChannel = null;

    const setupRealtime = (userId) => {
      // Unsubscribe existing if any
      if (realtimeChannel) supabase.removeChannel(realtimeChannel);

      realtimeChannel = supabase
        .channel('public:profiles')
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
          (payload) => {
            console.log("Realtime update received:", payload.new);
            // Merge with existing user state to prevent overwriting local-only fields if any
            setUser(prev => ({ ...prev, ...payload.new }));
            
            if (payload.new.game_stats) {
              setGameStats(payload.new.game_stats);
              localStorage.setItem('prize_blaster_stats', JSON.stringify(payload.new.game_stats));
            }
            window.userTokens = payload.new.points;
          }
        )
        .subscribe();
    };

    // Initialize Session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        fetchProfile(session.user.id);
        setupRealtime(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        if (userRef.current?.id !== session.user.id) {
          fetchProfile(session.user.id);
          setupRealtime(session.user.id);
        }
      } else {
        setUser(null);
        if (realtimeChannel) supabase.removeChannel(realtimeChannel);
        setLoading(false);
      }
    });

    authSubscription = data.subscription;

    return () => {
      if (authSubscription) authSubscription.unsubscribe();
      if (realtimeChannel) supabase.removeChannel(realtimeChannel);
    };
  }, []);

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;

      if (data) {
        setUser(data);
        if (data.game_stats) {
          setGameStats(data.game_stats);
          localStorage.setItem('prize_blaster_stats', JSON.stringify(data.game_stats));
        }
        window.userTokens = data.points;
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (userRef.current?.id) {
      await fetchProfile(userRef.current.id);
    }
  };

  const signUp = async (email, password, name, referralCode = null) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          referred_by_code: referralCode
        }
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setGameStats(DEFAULT_GAME_STATS);
    localStorage.removeItem('prize_blaster_stats');
  };

  // --- Data Updates (Safe) ---
  const updateProfile = async (updates) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    // Optimistic Update
    const updatedUser = { ...currentUser, ...updates };
    setUser(updatedUser);

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', currentUser.id);

      if (error) {
        throw error;
      }
    } catch (err) {
      console.error("Failed to sync profile:", err);
      // Revert if it's a critical sync failure to avoid mismatch
      fetchProfile(currentUser.id);
    }
  };

  const addPoints = async (points, source = "Game Reward") => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    // Optimistic Update locally
    const newPoints = (Number(currentUser.points) || 0) + points;
    setUser(prev => ({ ...prev, points: newPoints }));

    try {
      // Use RPC for atomic update to avoid race conditions
      const { error } = await supabase.rpc('increment_points', { amount: points, description: source });
      
      if (error) {
        console.warn("RPC failed, falling back to direct update", error);
        // Fallback to simple update if RPC fails (redundancy)
        await updateProfile({ points: newPoints });
      }
    } catch (err) {
      console.error("Failed to add points:", err);
      // Force refresh to get true server state
      refreshProfile();
    }
  };

  const redeemPoints = async (amount, details) => {
    const currentUser = userRef.current;
    if (currentUser && currentUser.points >= amount) {
      
      try {
        // 1. Insert Withdrawal Request FIRST
        // We do this first to ensure we don't deduct points if the request fails
        const { error: insertError } = await supabase.from('withdrawals').insert({
          email: details.email,
          gcash_number: details.gcashNumber,
          amount_php: details.amount,
          points_redeemed: amount,
          status: 'pending'
        });

        if (insertError) {
          console.error("Withdrawal insert failed:", insertError);
          alert("Network error: Could not submit withdrawal request. Please try again.");
          return false;
        }

        // 2. Deduct Points ONLY after successful insert
        const newPoints = currentUser.points - amount;
        const newHistory = [
          {
            id: Date.now(),
            type: 'redeem',
            amount: amount,
            description: `Redeemed to ${details.gcashNumber}`,
            date: new Date().toISOString()
          },
          ...(currentUser.history || [])
        ].slice(0, 50);

        // Update profile with new points
        await updateProfile({ points: newPoints, history: newHistory });
        
        // Also refresh profile from server to ensure perfect sync
        setTimeout(refreshProfile, 1000);

        return true;

      } catch (e) {
        console.error("Redemption Critical Error:", e);
        alert("An error occurred during redemption. Please check your connection.");
        return false;
      }
    }
    return false;
  };

  const generateReferralCode = async () => {
    if (!userRef.current) return;
    
    // Always generate locally first to update UI immediately
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // Try to save to DB
    await updateProfile({ referral_code: code });
    return code;
  };

  const showRewardAdWithReward = (rewardAmount) => {
    showRewardAd(rewardAmount)
      .then((result) => {
        if (result === 'completed') {
          safeAlert(`Reward: +${rewardAmount} Points!`);
          addPoints(rewardAmount, 'Ad Reward');
        }
      })
      .catch((err) => {
        console.warn("Ad not completed:", err);
      });
  };

  const closeAdModal = () => {
    setShowAdModal(false);
    if (adReward > 0) {
      showRewardAdWithReward(adReward);
      setAdReward(0);
    }
  };

  const updateGameStats = (game, score) => {
    const newStats = {
      ...gameStats,
      [game]: {
        ...gameStats[game] || { highScore: 0, gamesPlayed: 0 },
        highScore: Math.max((gameStats[game]?.highScore || 0), score),
        gamesPlayed: (gameStats[game]?.gamesPlayed || 0) + 1
      }
    };
    
    setGameStats(newStats);
    localStorage.setItem('prize_blaster_stats', JSON.stringify(newStats));
    
    const currentUser = userRef.current;
    if (currentUser) {
      updateProfile({ 
        games_played: (currentUser.games_played || 0) + 1,
        game_stats: newStats 
      });
    }
  };

  useEffect(() => {
    window.gameContext = {
      pause: () => setIsGamePaused(true),
      resume: () => setIsGamePaused(false)
    };
    return () => {
      window.gameContext = undefined;
    };
  }, []);

  const value = {
    user,
    setUser: updateProfile,
    loading,
    signIn,
    signUp,
    logout,
    gameStats,
    showAdModal,
    adReward,
    addPoints,
    showRewardAd: showRewardAdWithReward,
    showInterstitial,
    closeAdModal,
    updateGameStats,
    redeemPoints,
    isGamePaused,
    refreshProfile,
    generateReferralCode
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};